import { Star } from 'lucide-react';

import { cn } from '@/lib/cn';
import { formatInteger, formatNumber } from '@/lib/format';

interface StarRatingProps {
  rating: number;
  reviewCount?: number;
  size?: 'sm' | 'md';
  className?: string;
}

/** Five stars with a clipped partial star, plus an accessible text summary. */
export function StarRating({ rating, reviewCount, size = 'sm', className }: StarRatingProps) {
  const starSize = size === 'sm' ? 'size-3.5' : 'size-4';
  const percent = Math.max(0, Math.min(100, (rating / 5) * 100));

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <span className="relative inline-flex" aria-hidden="true">
        <span className="flex gap-0.5">
          {Array.from({ length: 5 }, (_, index) => (
            <Star key={index} className={cn(starSize, 'text-sand-400')} fill="currentColor" />
          ))}
        </span>
        <span className="absolute inset-0 overflow-hidden" style={{ width: `${percent}%` }}>
          <span className="flex gap-0.5">
            {Array.from({ length: 5 }, (_, index) => (
              <Star
                key={index}
                className={cn(starSize, 'shrink-0 text-warning-500')}
                fill="currentColor"
              />
            ))}
          </span>
        </span>
      </span>

      <span className="text-xs font-semibold text-brown-700">
        {formatNumber(rating)}
        {reviewCount !== undefined && (
          <span className="font-normal text-brown-700/70"> ({formatInteger(reviewCount)})</span>
        )}
      </span>

      <span className="sr-only">
        Avaliação {formatNumber(rating)} de 5
        {reviewCount !== undefined ? `, com ${formatInteger(reviewCount)} avaliações` : ''}
      </span>
    </div>
  );
}
